import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Globe } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LANGUAGES } from '../../context/LanguageContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

export function Portfolio({ onBriefClick }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState(null);
  
  const handleBriefClick = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=auction`);
  };

  const projects = [
    {
      id: 'pienergi',
      url: 'https://pienergi.com',
      images: {
        mobile: {
          webp: '/images/portfolio/pienergi-mobile.webp',
          jpg: '/images/portfolio/pienergi-mobile.jpg'
        },
        tablet: {
          webp: '/images/portfolio/pienergi-tablet.webp',
          jpg: '/images/portfolio/pienergi-tablet.jpg'
        },
        desktop: {
          webp: '/images/portfolio/pienergi-desktop.webp',
          jpg: '/images/portfolio/pienergi-desktop.jpg'
        }
      }
    },
    {
      id: 'lavryshyn',
      url: 'https://lavryshyn.de',
      images: {
        mobile: {
          webp: '/images/portfolio/lavryshyn-mobile.webp',
          jpg: '/images/portfolio/lavryshyn-mobile.jpg'
        },
        tablet: {
          webp: '/images/portfolio/lavryshyn-tablet.webp',
          jpg: '/images/portfolio/lavryshyn-tablet.jpg'
        },
        desktop: {
          webp: '/images/portfolio/lavryshyn-desktop.webp',
          jpg: '/images/portfolio/lavryshyn-desktop.jpg'
        }
      }
    },
    {
      id: 'bodyharmonic',
      url: 'https://bodyharmonic.de',
      images: {
        mobile: {
          webp: '/images/portfolio/bodyharmonic-mobile.webp',
          jpg: '/images/portfolio/bodyharmonic-mobile.jpg'
        },
        tablet: {
          webp: '/images/portfolio/bodyharmonic-tablet.webp',
          jpg: '/images/portfolio/bodyharmonic-tablet.jpg'
        },
        desktop: {
          webp: '/images/portfolio/bodyharmonic-desktop.webp',
          jpg: '/images/portfolio/bodyharmonic-desktop.jpg'
        }
      }
    }
  ];

  return (
    <section id="cases" className="py-24 md:py-32 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="text-white">{t('portfolio.title')}</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-primary/50 transition-all duration-300"
            >
              {/* Image */}
              <div 
                className="relative aspect-[16/10] overflow-hidden bg-slate-900 cursor-pointer"
                onClick={() => setSelectedProject(project)}
              >
                <picture>
                  {/* Desktop: 1024px+ */}
                  <source 
                    media="(min-width: 1024px)" 
                    srcSet={project.images.desktop.webp} 
                    type="image/webp" 
                  />
                  <source 
                    media="(min-width: 1024px)" 
                    srcSet={project.images.desktop.jpg} 
                    type="image/jpeg" 
                  />
                  
                  {/* Tablet: 640px - 1023px */}
                  <source 
                    media="(min-width: 640px)" 
                    srcSet={project.images.tablet.webp} 
                    type="image/webp" 
                  />
                  <source 
                    media="(min-width: 640px)" 
                    srcSet={project.images.tablet.jpg} 
                    type="image/jpeg" 
                  />
                  
                  {/* Mobile: < 640px */}
                  <source 
                    srcSet={project.images.mobile.webp} 
                    type="image/webp" 
                  />
                  <img
                    src={project.images.mobile.jpg}
                    alt={t(`portfolio.projects.${project.id}.title`)}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </picture>
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                
                {/* Live badge */}
                <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400 font-medium">{t('portfolio.live')}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                  {t(`portfolio.projects.${project.id}.title`)}
                </h3>
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                  {t(`portfolio.projects.${project.id}.description`)}
                </p>

                {/* CTA */}
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary-light transition-colors group/link"
                >
                  <Globe size={18} />
                  <span className="font-medium">{t('portfolio.viewSite')}</span>
                  <ExternalLink size={16} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-slate-300 text-lg mb-6">
            {t('portfolio.cta')}
          </p>
          <Button 
            size="lg"
            className="magnetic-btn"
            onClick={handleBriefClick}
          >
            {t('portfolio.ctaButton')}
          </Button>
        </motion.div>
      </div>

      {/* Project Preview Modal */}
      <Modal 
        isOpen={!!selectedProject} 
        onClose={() => setSelectedProject(null)}
        className="max-w-4xl"
      >
        {selectedProject && (
          <div className="p-6">
            {/* Project Title */}
            <h3 className="font-heading text-2xl font-bold text-white mb-4">
              {t(`portfolio.projects.${selectedProject.id}.title`)}
            </h3>

            {/* Screenshot Preview */}
            <div className="relative rounded-lg bg-slate-950 mb-6 max-h-[60vh] flex items-center justify-center">
              <picture>
                {/* Desktop: 1024px+ */}
                <source 
                  media="(min-width: 1024px)" 
                  srcSet={selectedProject.images.desktop.webp} 
                  type="image/webp" 
                />
                <source 
                  media="(min-width: 1024px)" 
                  srcSet={selectedProject.images.desktop.jpg} 
                  type="image/jpeg" 
                />
                
                {/* Tablet: 640px - 1023px */}
                <source 
                  media="(min-width: 640px)" 
                  srcSet={selectedProject.images.tablet.webp} 
                  type="image/webp" 
                />
                <source 
                  media="(min-width: 640px)" 
                  srcSet={selectedProject.images.tablet.jpg} 
                  type="image/jpeg" 
                />
                
                {/* Mobile: < 640px */}
                <source 
                  srcSet={selectedProject.images.mobile.webp} 
                  type="image/webp" 
                />
                <img
                  src={selectedProject.images.mobile.jpg}
                  alt={t(`portfolio.projects.${selectedProject.id}.title`)}
                  className="w-full h-auto max-h-[60vh] object-contain rounded-lg"
                />
              </picture>
            </div>

            {/* Description */}
            <p className="text-slate-300 mb-6">
              {t(`portfolio.projects.${selectedProject.id}.description`)}
            </p>

            {/* Visit Site Button */}
            <a
              href={selectedProject.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary hover:bg-primary-light text-white font-medium transition-colors"
            >
              <Globe size={20} />
              <span>{t('portfolio.visitSite')}</span>
              <ExternalLink size={18} />
            </a>
          </div>
        )}
      </Modal>
    </section>
  );
}
